import {
    type ButtonHTMLAttributes,
    Children,
    cloneElement,
    isValidElement,
    type ReactElement,
    type ReactNode,
} from 'react';
import { Slot } from '@radix-ui/react-slot';

import { classNames } from '@m1work/shared-utils';

import styles from './Menu.module.scss';

export type MenuItemColor = 'primary' | 'accent' | 'error' | 'success' | 'soft';
export type MenuItemSize = 'sm' | 'md' | 'lg';

export interface MenuItemProps extends ButtonHTMLAttributes<HTMLButtonElement> {
    icon?: ReactNode;
    /** Colour role. @default 'primary' */
    color?: MenuItemColor;
    /** @default 'md' */
    size?: MenuItemSize;
    /** Render the child (the app's link with the label inside) instead of a `<button>`. @default false */
    asChild?: boolean;
}

/**
 * One action in a `Menu`: a button, or with `asChild` the app's link. The label is the children (or the link's children).
 *
 * @example
 * <MenuItem icon={<OrdersIcon />} asChild>
 *     <Link href="/orders">{t('menu.orders')}</Link>
 * </MenuItem>
 */
export function MenuItem({
    icon,
    color = 'primary',
    size = 'md',
    asChild = false,
    className,
    children,
    ...props
}: MenuItemProps): ReactElement {
    const classes = classNames(styles.item, styles[`color-${color}`], styles[`size-${size}`], className);

    const renderContent = (label: ReactNode): ReactElement => (
        <>
            {icon && <span className={styles.icon}>{icon}</span>}
            <span className={styles.label}>{label}</span>
        </>
    );

    if (asChild) {
        const child = Children.only(children);

        if (isValidElement<{ children?: ReactNode }>(child)) {
            // Slottable can't wrap the link's own text in the label span, so the link is cloned with the content.
            return (
                <Slot role="menuitem" className={classes} {...props}>
                    {cloneElement(child, undefined, renderContent(child.props.children))}
                </Slot>
            );
        }
    }

    return (
        <button type="button" role="menuitem" className={classes} {...props}>
            {renderContent(children)}
        </button>
    );
}
